import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Mail, Lock, User } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/App";

export default function Login() {
  const { login, loading, checkAuth } = useAuth();
  const [activeTab, setActiveTab] = useState("login");
  const [submitting, setSubmitting] = useState(false);
  
  // Login form
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  
  // Register form
  const [registerName, setRegisterName] = useState("");
  const [registerEmail, setRegisterEmail] = useState("");
  const [registerPassword, setRegisterPassword] = useState("");
  const [registerPasswordConfirm, setRegisterPasswordConfirm] = useState("");
  const [inviteToken, setInviteToken] = useState("");
  
  // Detect invite token + OAuth errors from URL
  // Detect invite token + OAuth errors from URL (intentionally runs once on mount)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const invite = params.get("invite");
    if (invite) {
      setInviteToken(invite);
      setActiveTab("register");
    }
    const err = params.get("error");
    if (err === "invite_required") {
      toast.error("Acceso solo por invitación. Contacta con el administrador.");
    } else if (err === "account_disabled") {
      toast.error("Tu cuenta está desactivada. Contacta con el administrador.");
    } else if (err === "auth_failed") {
      toast.error("Error de autenticación con Google.");
    } else if (err === "oauth_not_configured") {
      toast.error("Google OAuth no configurado. Usa email y contraseña.");
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const response = await apiClient.post("/auth/login", {
        email: loginEmail,
        password: loginPassword
      });
      
      localStorage.setItem("auth_token", response.data.token);
      toast.success("¡Bienvenido de vuelta!");
      window.location.href = "/";
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al iniciar sesión");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (registerPassword !== registerPasswordConfirm) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    
    if (registerPassword.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await apiClient.post("/auth/register", {
        email: registerEmail,
        password: registerPassword,
        name: registerName,
        invite_token: inviteToken || null
      });
      
      localStorage.setItem("auth_token", response.data.token);
      toast.success("¡Cuenta creada exitosamente!");
      window.location.href = "/";
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear cuenta");
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      const response = await apiClient.get("/auth/google");
      window.location.href = response.data.authorization_url;
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.includes("no configurado")) {
        toast.error("Google OAuth no está configurado. Usa email y contraseña.");
      } else {
        toast.error(detail || "Error al conectar con Google");
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#020408] grid-bg flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex flex-col items-center mb-10">
          <img
            src="/logo.png"
            alt="adsFunnel"
            className="w-20 h-20 object-contain mb-5 animate-pulse-glow rounded-2xl"
            data-testid="login-logo"
          />
          <h1 className="text-3xl font-bold text-white tracking-tight">
            ads<span className="brand-gradient-text">Funnel</span>
          </h1>
          <p className="text-slate-500 mt-2 text-center text-sm max-w-xs">
            Analiza anuncios de competidores con inteligencia artificial
          </p>
        </div>

        {/* Auth Card */}
        <div className="glass rounded-2xl p-6 animate-fade-in">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-slate-900/50 border border-white/5 mb-6">
              <TabsTrigger 
                value="login" 
                className="data-[state=active]:bg-cyan-600 data-[state=active]:text-white"
                data-testid="tab-login"
              >
                Iniciar Sesión
              </TabsTrigger>
              <TabsTrigger 
                value="register" 
                className="data-[state=active]:bg-cyan-600 data-[state=active]:text-white"
                data-testid="tab-register"
              >
                Registrarse
              </TabsTrigger>
            </TabsList>

            {/* Login Tab */}
            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="login-email" className="text-slate-300">Email</Label>
                  <div className="relative mt-1.5">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      id="login-email"
                      type="email"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      placeholder="tu@email.com"
                      className="pl-10 bg-slate-950/50 border-white/10 text-white placeholder:text-slate-600 focus:border-cyan-500/50"
                      required
                      data-testid="login-email-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="login-password" className="text-slate-300">Contraseña</Label>
                  <div className="relative mt-1.5">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      id="login-password"
                      type="password"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      placeholder="••••••••"
                      className="pl-10 bg-slate-950/50 border-white/10 text-white placeholder:text-slate-600 focus:border-cyan-500/50"
                      required
                      data-testid="login-password-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-cyan-600 hover:bg-cyan-500 text-white py-5 btn-glow"
                  data-testid="login-submit-btn"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : null}
                  Iniciar Sesión
                </Button>
              </form>
            </TabsContent>

            {/* Register Tab */}
            <TabsContent value="register">
              {inviteToken ? (
                <div className="mb-4 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                  <p className="text-xs text-emerald-300">
                    ✓ Invitación válida detectada. Completa tus datos para crear tu cuenta.
                  </p>
                </div>
              ) : (
                <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                  <p className="text-xs text-amber-300">
                    El registro es solo por invitación. Si no tienes una, contacta con el administrador.
                  </p>
                </div>
              )}
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <Label htmlFor="register-name" className="text-slate-300">Nombre</Label>
                  <div className="relative mt-1.5">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      id="register-name"
                      type="text"
                      value={registerName}
                      onChange={(e) => setRegisterName(e.target.value)}
                      placeholder="Tu nombre"
                      className="pl-10 bg-slate-950/50 border-white/10 text-white placeholder:text-slate-600 focus:border-cyan-500/50"
                      required
                      data-testid="register-name-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="register-email" className="text-slate-300">Email</Label>
                  <div className="relative mt-1.5">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      id="register-email"
                      type="email"
                      value={registerEmail}
                      onChange={(e) => setRegisterEmail(e.target.value)}
                      placeholder="tu@email.com"
                      className="pl-10 bg-slate-950/50 border-white/10 text-white placeholder:text-slate-600 focus:border-cyan-500/50"
                      required
                      data-testid="register-email-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="register-password" className="text-slate-300">Contraseña</Label>
                  <div className="relative mt-1.5">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      id="register-password"
                      type="password"
                      value={registerPassword}
                      onChange={(e) => setRegisterPassword(e.target.value)}
                      placeholder="Mínimo 6 caracteres"
                      className="pl-10 bg-slate-950/50 border-white/10 text-white placeholder:text-slate-600 focus:border-cyan-500/50"
                      required
                      data-testid="register-password-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="register-password-confirm" className="text-slate-300">Confirmar Contraseña</Label>
                  <div className="relative mt-1.5">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      id="register-password-confirm"
                      type="password"
                      value={registerPasswordConfirm}
                      onChange={(e) => setRegisterPasswordConfirm(e.target.value)}
                      placeholder="Repite tu contraseña"
                      className="pl-10 bg-slate-950/50 border-white/10 text-white placeholder:text-slate-600 focus:border-cyan-500/50"
                      required
                      data-testid="register-password-confirm-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-cyan-600 hover:bg-cyan-500 text-white py-5 btn-glow"
                  data-testid="register-submit-btn"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : null}
                  Crear Cuenta
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10"></div>
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-slate-900 px-3 text-slate-500">o continúa con</span>
            </div>
          </div>

          {/* Google Login */}
          <Button
            type="button"
            onClick={handleGoogleLogin}
            disabled={loading}
            variant="outline"
            className="w-full border-white/10 bg-white/5 hover:bg-white/10 text-white py-5"
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Google
          </Button>
        </div>

        {/* Features */}
        <div className="mt-10 grid grid-cols-3 gap-4 text-center">
          <div className="p-3">
            <div className="text-xl font-bold text-cyan-400">Meta</div>
            <div className="text-xs text-slate-500 mt-0.5">Ads Library</div>
          </div>
          <div className="p-3">
            <div className="text-xl font-bold text-cyan-400">LinkedIn</div>
            <div className="text-xs text-slate-500 mt-0.5">Ads Library</div>
          </div>
          <div className="p-3">
            <div className="text-xl font-bold text-cyan-400">Google</div>
            <div className="text-xs text-slate-500 mt-0.5">Ads Transparency</div>
          </div>
        </div>
      </div>
    </div>
  );
}
